import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, fmtCurrency } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { useAIContext } from "@/context/AIContextProvider";
import { TID } from "@/constants/testIds";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DataCompletenessPanel } from "@/components/performance/DataCompletenessPanel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShieldAlert, DollarSign, HardHat, MessageSquare, Zap, Loader2, Calculator, TrendingUp, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { BIGauge } from "@/components/perf/BIGauge";
import { KpiDrillDown } from "@/components/perf/KpiDrillDown";
import { JsaExemptions } from "@/components/perf/JsaExemptions";
import { BUCompare } from "@/components/perf/BUCompare";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  Tooltip, ReferenceLine, CartesianGrid, Legend,
} from "recharts";

// Compute the 12 months (YYYY-MM + human label) for a given AU FY.
// FY27 = Jul-2026 → Jun-2027. Dropped the old hardcoded FY26 list
// so the picker automatically extends into future FYs.
const monthsForFY = (fy) => {
  if (!fy || !fy.toUpperCase().startsWith("FY")) return [];
  const endYear = 2000 + parseInt(fy.slice(2), 10);  // FY27 → 2027
  const startYear = endYear - 1;
  const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const out = [];
  for (let i = 0; i < 12; i++) {
    const m = 7 + i;
    const y = startYear + (m > 12 ? 1 : 0);
    const mm = m > 12 ? m - 12 : m;
    out.push({
      code: `${y}-${String(mm).padStart(2, "0")}`,
      label: `${MON[mm - 1]} ${String(y).slice(-2)}`,
    });
  }
  return out;
};

const KRA_STYLES = {
  financials: { bg: "bg-emerald-50",  border: "border-emerald-200", title: "text-emerald-900", icon: DollarSign,     label: "Financials" },
  safety:     { bg: "bg-rose-50",     border: "border-rose-200",    title: "text-rose-900",    icon: HardHat,        label: "Safety" },
  customer:   { bg: "bg-slate-100",   border: "border-slate-300",   title: "text-slate-900",   icon: MessageSquare,  label: "Customer Service" },
  performance:{ bg: "bg-amber-50",    border: "border-amber-200",   title: "text-amber-900",   icon: Zap,            label: "Performance" },
};

/**
 * TrendAlertsStrip — cross-BU early-warning ribbon at the top of the
 * BU Performance dashboard. Fetches /api/qms/trend-alerts (which scans
 * every active BU's last 3 months of MYOB actuals) and renders a
 * compact card for each firing alert. Only appears when at least one
 * BU is drifting — otherwise the ribbon collapses silently.
 */
function TrendAlertsStrip({ endMonth, currentBuId, onSelectBU }) {
  const [alerts, setAlerts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!endMonth) return;
    setBusy(true);
    api.get(`/qms/trend-alerts?end_month=${endMonth}&lookback_months=3`)
      .then((r) => setAlerts(r.data))
      .catch(() => setAlerts(null))
      .finally(() => setBusy(false));
  }, [endMonth]);

  if (busy || !alerts) return null;
  if (alerts.alert_count === 0) return null;

  const highCount = alerts.high_severity_count || 0;
  const totalCount = alerts.alert_count;
  const shown = expanded ? alerts.alerts : alerts.alerts.slice(0, 3);

  return (
    <div className="mb-4 rounded-2xl border border-amber-300 bg-gradient-to-r from-amber-50 to-white p-3 sm:p-4"
         data-testid="perf-trend-alerts">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <AlertTriangle className="w-4 h-4" />
          Attention Needed
          <span className="text-[11px] font-normal text-amber-800">
            · {totalCount} alert{totalCount === 1 ? "" : "s"} across your BUs
            {highCount > 0 && <span className="ml-1 text-rose-700 font-medium">({highCount} high)</span>}
          </span>
        </div>
        {alerts.alerts.length > 3 && (
          <button type="button"
                  onClick={() => setExpanded((e) => !e)}
                  data-testid="perf-alerts-toggle"
                  className="text-[11px] text-amber-800 hover:text-amber-900 underline">
            {expanded ? "Show fewer" : `Show all ${alerts.alerts.length}`}
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {shown.map((a, i) => {
          const isCurrent = a.business_unit.id === currentBuId;
          const sevCls = a.severity === "high"
            ? "border-rose-300 bg-rose-50 text-rose-900"
            : "border-amber-300 bg-white text-amber-900";
          const badgeCls = a.severity === "high"
            ? "bg-rose-100 text-rose-700 border-rose-200"
            : "bg-amber-100 text-amber-800 border-amber-200";
          return (
            <button type="button"
                    key={i}
                    onClick={() => onSelectBU?.(a.business_unit.id)}
                    disabled={isCurrent}
                    data-testid={`perf-alert-${a.business_unit.code}-${a.kind}`}
                    className={
                      "text-left rounded-lg border p-2.5 transition " + sevCls +
                      (isCurrent ? " ring-2 ring-emerald-300 opacity-95 cursor-default"
                                 : " hover:shadow-md hover:scale-[1.01] cursor-pointer")
                    }
                    title={isCurrent ? "This BU is currently selected" : `Switch to ${a.business_unit.code}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${badgeCls}`}>
                  {a.severity}
                </span>
                <span className="text-[11px] font-semibold">{a.business_unit.code}</span>
                <span className="text-[10px] opacity-70 truncate">{a.business_unit.name}</span>
              </div>
              <div className="text-[11px] leading-tight opacity-90">{a.message}</div>
            </button>
          );
        })}
      </div>
      <div className="mt-2 text-[10px] text-amber-700">
        Thresholds: sales &lt; {(alerts.thresholds.sales_vs_budget_floor * 100).toFixed(0)}% budget or GP &lt; {(alerts.thresholds.gp_pct_floor * 100).toFixed(0)}% for {alerts.thresholds.consecutive_streak} months in a row, or MoM sales drop ≥ {(alerts.thresholds.mom_sales_drop_floor * 100).toFixed(0)}%.
      </div>
    </div>
  );
}


function FinancialsTrendChart({ buId, endMonth }) {
  const [trend, setTrend] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!buId || !endMonth) return;
    setBusy(true);
    api.get(`/qms/financials-trend?business_unit_id=${buId}&end_month=${endMonth}&months=12`)
      .then((r) => setTrend(r.data))
      .catch(() => setTrend(null))
      .finally(() => setBusy(false));
  }, [buId, endMonth]);

  const chartData = useMemo(() => {
    if (!trend?.trend) return [];
    return trend.trend.map((r) => {
      const [y, m] = r.period_month.split("-");
      const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      return {
        month: `${MON[parseInt(m,10)-1]} ${y.slice(-2)}`,
        sales: r.sales,
        budget: r.budget_sales,
        gpPct: r.gp_pct,
      };
    });
  }, [trend]);

  const gpTarget = trend?.gp_target_pct || 38;
  const hasData = chartData.some((d) => d.sales > 0 || d.budget > 0);

  return (
    <div className="mt-4 rounded-2xl border border-emerald-200 bg-white p-4 sm:p-5"
         data-testid="perf-financials-trend">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 font-display text-sm font-semibold text-emerald-900">
          <TrendingUp className="w-4 h-4" /> 12-Month Momentum
          <span className="ml-1 text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded">
            Sales · Budget · GP %
          </span>
        </div>
        {trend && (
          <div className="text-[11px] text-slate-500 tabular-nums">
            {chartData.length} months to {chartData[chartData.length - 1]?.month || "—"}
          </div>
        )}
      </div>
      {busy && <div className="h-56 shimmer rounded-md" />}
      {!busy && !hasData && (
        <div className="h-56 flex items-center justify-center text-xs text-slate-500 border border-dashed border-slate-200 rounded-md">
          No trend data for this window yet.
        </div>
      )}
      {!busy && hasData && (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis yAxisId="left"  tick={{ fontSize: 11, fill: "#64748b" }}
                     tickFormatter={(v) => `$${Math.round(v / 1000)}k`} width={54} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#64748b" }}
                     tickFormatter={(v) => `${v}%`}
                     domain={[0, (max) => Math.max(60, Math.ceil(max * 1.1))]} width={40} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
                formatter={(value, name) => {
                  if (name === "GP %") return [`${Number(value).toFixed(1)}%`, name];
                  return [fmtCurrency(Number(value)), name];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
              <ReferenceLine yAxisId="right" y={gpTarget} stroke="#64748b"
                             strokeDasharray="4 4"
                             label={{ value: `GP target ${gpTarget}%`, position: "insideTopRight",
                                      fill: "#64748b", fontSize: 10 }} />
              <Bar  yAxisId="left"  dataKey="budget" name="Budget" fill="#cbd5e1" radius={[3, 3, 0, 0]} />
              <Bar  yAxisId="left"  dataKey="sales"  name="Sales"  fill="#10b981" radius={[3, 3, 0, 0]} />
              <Line yAxisId="right" dataKey="gpPct"  name="GP %"   stroke="#0f766e"
                    strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}


function FinancialsPanel({ data }) {
  const meta = KRA_STYLES.financials;
  const Icon = meta.icon;
  if (data?.restricted) {
    return (
      <div className={`rounded-2xl border ${meta.border} ${meta.bg} p-5`} data-testid="perf-financials-restricted">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon className={`w-4 h-4 ${meta.title}`} />
            <span className={`font-display text-sm font-semibold ${meta.title}`}>Financials</span>
          </div>
          <span className="text-[11px] font-medium text-slate-500">weight {data.weight_pct}%</span>
        </div>
        <div className="text-3xl font-display font-bold text-slate-900">{data.score_pct}%</div>
        <div className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
          <ShieldAlert className="w-3.5 h-3.5" /> Financial detail restricted for your role — score only
        </div>
      </div>
    );
  }
  const m = data.myob || {};
  const diag = m.diagnostic || {};
  const noData = (m.sales || 0) === 0 && (m.cogs || 0) === 0;
  const salesPct = m.sales_vs_budget_pct || 0;
  const variance = (m.sales || 0) - (m.budget_sales || 0);
  const gpPct = m.gp_pct || 0;
  const gpTargetPct = 36;
  const gpZoneColor =
    gpPct >= gpTargetPct ? "bg-emerald-500"
    : gpPct >= gpTargetPct * 0.8 ? "bg-amber-500"
    : "bg-rose-500";
  const salesZoneColor =
    salesPct >= 100 ? "bg-emerald-500"
    : salesPct >= 80 ? "bg-amber-500"
    : "bg-rose-500";
  // Bar chart proportions (capped at 150%)
  const salesBarW = Math.min(150, Math.max(0, salesPct));

  return (
    <div className={`rounded-2xl border ${meta.border} ${meta.bg} p-5`}
         data-testid="perf-financials-panel">
      <div className="flex items-center justify-between mb-4">
        <div className={`flex items-center gap-2 font-display text-lg font-semibold ${meta.title}`}>
          <Icon className="w-5 h-5" /> {meta.label}
          <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500 bg-white/60 border border-slate-300 px-1.5 py-0.5 rounded">
            MYOB · {m.source ? "actuals" : "no data"}
          </span>
        </div>
        <div className="text-xs text-slate-600">
          <span className="tabular-nums font-semibold">{data.score_pct.toFixed(2)}%</span>
          <span className="text-slate-400"> of {data.weight_pct}%</span>
        </div>
      </div>

      {/* Sales vs Budget bar */}
      {noData && diag.reason ? (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900"
             data-testid="perf-financials-empty-reason">
          <div className="font-semibold mb-1">No MYOB data for this month yet</div>
          {diag.reason === "no_data_for_month" && (
            <div>
              <div>MYOB has <b>0</b> P&L rows for this period. Either MYOB isn't synced through this month yet, or the P&L Report hasn't been posted for the month.</div>
              <div className="mt-1">→ Check <b>Finance → MYOB Sync Health</b> and trigger a fresh sync.</div>
            </div>
          )}
          {diag.reason === "no_mapping_for_bu" && (
            <div>
              <div>MYOB has <b>{diag.actual_rows_this_month}</b> P&L rows for this period, but <b>{diag.unmapped_accounts_seen}</b> account(s) haven't been mapped to a Business Unit yet.</div>
              {diag.unmapped_sample?.length > 0 && (
                <div className="mt-1">Unmapped sample: <code className="font-mono">{diag.unmapped_sample.join(", ")}</code></div>
              )}
              <div className="mt-1">→ Go to <b>Finance → BU Mapping</b> and add these accounts to the appropriate BUs.</div>
            </div>
          )}
        </div>
      ) : (
      <div className="mb-4">
        <div className="flex items-baseline justify-between mb-1">
          <div className="text-xs uppercase tracking-wider text-slate-500">Sales</div>
          <div className="text-xs text-slate-600 tabular-nums">
            <b>{fmtCurrency(m.sales || 0)}</b> vs budget {fmtCurrency(m.budget_sales || 0)}
          </div>
        </div>
        <div className="relative h-6 bg-white border border-slate-200 rounded-md overflow-hidden">
          {/* 100% (budget) marker */}
          <div className="absolute inset-y-0 border-r-2 border-slate-400 border-dashed z-10"
               style={{ left: "66.66%" }} />
          {/* Actual sales bar */}
          <div className={`h-full ${salesZoneColor} transition-all`}
               style={{ width: `${(salesBarW / 150) * 100}%` }} />
          {/* Label */}
          <div className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold text-slate-800 mix-blend-difference invert">
            {salesPct.toFixed(0)}% of budget
          </div>
        </div>
        <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
          <span>$0</span>
          <span>Budget</span>
          <span>150%</span>
        </div>
        <div className={`text-[11px] mt-1 tabular-nums ${variance >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
          {variance >= 0 ? "+" : ""}{fmtCurrency(variance)} vs budget
        </div>
      </div>
      )}

      {/* GP $ + GP% side-by-side */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white/70 border border-slate-200 rounded-md p-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Gross Profit</div>
          <div className="font-display text-2xl font-bold tabular-nums text-emerald-800">
            {fmtCurrency(m.gp || 0)}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Sales {fmtCurrency(m.sales || 0)} − COGS {fmtCurrency(m.cogs || 0)}
          </div>
        </div>
        <div className="bg-white/70 border border-slate-200 rounded-md p-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">GP % (target 36%)</div>
          <div className={`font-display text-2xl font-bold tabular-nums ${gpZoneColor.replace("bg-", "text-")}`}>
            {gpPct.toFixed(1)}%
          </div>
          {/* GP bar with target marker */}
          <div className="relative h-2 bg-slate-100 rounded mt-2 overflow-hidden">
            <div className="absolute inset-y-0 border-r-2 border-slate-500 z-10"
                 style={{ left: `${Math.min(gpTargetPct / 60 * 100, 100)}%` }} />
            <div className={`h-full ${gpZoneColor}`}
                 style={{ width: `${Math.min(Math.max(gpPct, 0) / 60 * 100, 100)}%` }} />
          </div>
        </div>
      </div>
    </div>
  );
}


function KRAPanel({ kraKey, data, onDrill }) {
  const meta = KRA_STYLES[kraKey];
  const Icon = meta.icon;
  const drillable = kraKey !== "financials";
  return (
    <div className={`rounded-2xl border ${meta.border} ${meta.bg} p-5`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`flex items-center gap-2 font-display text-lg font-semibold ${meta.title}`}>
          <Icon className="w-5 h-5" /> {meta.label}
        </div>
        <div className="text-xs text-slate-600">
          <span className="tabular-nums font-semibold">{data.score_pct.toFixed(2)}%</span>
          <span className="text-slate-400"> of {data.weight_pct}%</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {data.gauges.map((g) => (
          <button key={g.code || g.name} type="button"
                  data-testid={`kpi-gauge-${g.code}`}
                  disabled={!drillable}
                  onClick={() => drillable && onDrill?.(g)}
                  title={[
                    g.calc,
                    g.target_min != null ? `Target: ${g.target_min}${g.unit === "pct" ? " (as %)" : ""}` : null,
                    drillable ? "Click to see the data behind this score" : null,
                  ].filter(Boolean).join("\n")}
                  className={"rounded-xl p-1 text-left transition " +
                             (drillable ? "cursor-pointer hover:bg-white/70 hover:shadow-sm" : "cursor-default")}>
            <BIGauge label={g.name}
                     value={g.value}
                     unit={g.unit}
                     min={g.min}
                     max={g.max}
                     zones={g.zones} />
            {g.calc && (
              <p className="mt-0.5 px-1 text-[9px] leading-tight text-slate-400 line-clamp-2"
                 data-testid={`kpi-calc-${g.code}`}>{g.calc}</p>
            )}
          </button>
        ))}
      </div>
      {drillable && (
        <div className="mt-2 text-[10px] text-slate-400 text-right">Click a dial to interrogate the data</div>
      )}
    </div>
  );
}

function EmpSection({ title, rows, bus, onChange, savingId, disabled, highlight }) {
  return (
    <div>
      <div className={`text-xs font-semibold uppercase tracking-wider mb-2 ${highlight ? "text-amber-700" : "text-slate-500"}`}>{title}</div>
      {rows.length === 0 && <div className="text-xs text-slate-400">None.</div>}
      <div className="space-y-1.5">
        {rows.map((e) => (
          <div key={e.id} className="flex items-center gap-3 rounded-md border border-slate-200 px-3 py-2"
               data-testid={`emp-row-${e.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{e.name}</div>
              <div className="text-[11px] text-slate-500 truncate">{e.role || "—"}{e.email ? ` · ${e.email}` : ""}</div>
            </div>
            <Select value={e.business_unit_id || "none"} disabled={disabled || savingId === e.id}
                    onValueChange={(v) => onChange(e.id, v === "none" ? null : v)}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid={`emp-bu-select-${e.id}`}>
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {bus.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} · {b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {savingId === e.id && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          </div>
        ))}
      </div>
    </div>
  );
}


export default function PerformancePage() {
  const { can, loaded } = usePermissions();
  const { setContext } = useAIContext();
  const [bus, setBus] = useState([]);
  const [buId, setBuId] = useState(null);
  const [fy, setFy] = useState(() => {
    const now = new Date();
    // AU FY: FY27 = Jul-2026 → Jun-2027
    const y = now.getMonth() + 1 >= 7 ? now.getFullYear() + 1 : now.getFullYear();
    return `FY${String(y).slice(-2)}`;
  });
  // Default month = latest completed month (end of current calendar month)
  // rather than a hardcoded FY26 value. Automatically stays in sync when
  // the FY dropdown changes (see useEffect below).
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [dash, setDash] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drillKpi, setDrillKpi] = useState(null);
  const [showExemptions, setShowExemptions] = useState(false);

  // Dropdown shows PARENT business units only (JN200/300/400/600/700).
  // Child BUs are folded into their hundreds-family parent (trailing 3
  // digits → X00, e.g. BU610 HVAC → JN600, JN410 → JN400) — selecting the
  // parent already rolls up the children's employees, jobs, KPIs &
  // financials in /qms/dashboard, so we hide the children here. This mirrors
  // the server-side bu_rollup.child_parent_map logic exactly.
  const parentBus = useMemo(() => {
    const fam3 = (code) => {
      const m = String(code || "").match(/(\d{3})$/);
      return m ? m[1] : null;
    };
    const present = new Set(bus.map((b) => fam3(b.code)).filter(Boolean));
    const isChild = (b) => {
      const t = fam3(b.code);
      if (!t) return false;
      const parent3 = t[0] + "00";
      return t !== parent3 && present.has(parent3);
    };
    return bus.filter((b) => !isChild(b));
  }, [bus]);

  // child BU id → parent BU id (so a trend-alert click on a child jumps to
  // the parent family view that's actually selectable in the dropdown).
  const parentIdOf = useMemo(() => {
    const fam3 = (c) => {
      const m = String(c || "").match(/(\d{3})$/);
      return m ? m[1] : null;
    };
    const byFam = {};
    bus.forEach((b) => { const t = fam3(b.code); if (t) byFam[t] = b.id; });
    const map = {};
    bus.forEach((b) => {
      const t = fam3(b.code);
      if (!t) return;
      const pid = byFam[t[0] + "00"];
      if (pid && pid !== b.id) map[b.id] = pid;
    });
    return map;
  }, [bus]);
  const selectBU = (id) => setBuId(parentIdOf[id] || id);

  // Keep the month value valid for the currently-selected FY. If the
  // user was on 2026-06 (FY26) then switches to FY27, snap to the last
  // month of FY27 that isn't in the future, otherwise the first month
  // of the FY. Prevents the "no data" empty state that appears when the
  // stale month is outside the selected FY's window.
  useEffect(() => {
    if (!fy || !fy.toUpperCase().startsWith("FY")) return;
    const yr = parseInt(fy.slice(2), 10);
    const startYear = 2000 + yr - 1;   // FY27 → 2026
    const months = [];
    for (let i = 0; i < 12; i++) {
      const m = 7 + i;
      const y = startYear + (m > 12 ? 1 : 0);
      const mm = m > 12 ? m - 12 : m;
      months.push(`${y}-${String(mm).padStart(2, "0")}`);
    }
    if (!months.includes(month)) {
      const now = new Date();
      const nowYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      // Prefer the latest month in this FY that is <= current month;
      // fall back to the first month (Jul) if the whole FY is future.
      const inPast = months.filter((m) => m <= nowYm);
      setMonth(inPast.length ? inPast[inPast.length - 1] : months[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fy]);

  useEffect(() => {
    if (!loaded || !can("performance", "view")) return;
    api.get("/org/business-units").then((r) => {
      const active = (r.data || []).filter((b) => b.active !== false);
      setBus(active);
      if (active.length && !buId) setBuId(active.find((b) => b.code === "JN400")?.id || active[0].id);
    }).catch(() => {});
    /* eslint-disable-next-line */
  }, [loaded]);

  // Land on the latest month that actually has Jobs/Completed-Tasks data.
  // The current (in-progress) month has no monthly import yet, so defaulting
  // to it left every hero tile — including Jobs Completed — reading 0.
  const [dataMonthApplied, setDataMonthApplied] = useState(false);
  const [latestDataMonth, setLatestDataMonth] = useState(null);
  useEffect(() => {
    if (!loaded || !can("performance", "view") || dataMonthApplied) return;
    api.get("/qms/latest-data-month").then((r) => {
      const lm = r.data?.latest_month;
      setDataMonthApplied(true);
      if (!lm || !/^\d{4}-\d{2}$/.test(lm)) return;
      setLatestDataMonth(lm);
      const [y, mm] = lm.split("-").map(Number);
      const fyEnd = mm >= 7 ? y + 1 : y;
      setFy(`FY${String(fyEnd).slice(-2)}`);
      setMonth(lm);
    }).catch(() => setDataMonthApplied(true));
    /* eslint-disable-next-line */
  }, [loaded]);

  // Warn when employees carry an unresolved Business Unit (they're excluded
  // from every BU's headcount/KPIs until re-synced).
  const [unresolvedBU, setUnresolvedBU] = useState(0);
  useEffect(() => {
    if (!loaded || !can("performance", "view")) return;
    api.get("/imports/employees/unresolved-business-units")
      .then((r) => setUnresolvedBU(r.data?.count || 0)).catch(() => {});
    /* eslint-disable-next-line */
  }, [loaded]);

  // Clickable Employees tile → review the BU's staff and move them to the
  // correct Business Unit (handles mis-tagged and unassigned people).
  const [empOpen, setEmpOpen] = useState(false);
  const [empData, setEmpData] = useState(null);
  const [empLoading, setEmpLoading] = useState(false);
  const [empSavingId, setEmpSavingId] = useState(null);
  const canEditBU = can("performance", "edit");
  const openEmpDialog = async () => {
    if (!buId) return;
    setEmpOpen(true); setEmpLoading(true); setEmpData(null);
    try {
      const { data } = await api.get(`/qms/bu-employees?business_unit_id=${buId}`);
      setEmpData(data);
    } catch (e) { toast.error("Failed to load employees"); }
    finally { setEmpLoading(false); }
  };
  const reassignEmp = async (empId, newBuId) => {
    setEmpSavingId(empId);
    try {
      await api.patch(`/qms/bu-employees/${empId}`, { business_unit_id: newBuId || null });
      const { data } = await api.get(`/qms/bu-employees?business_unit_id=${buId}`);
      setEmpData(data);
      toast.success("Business Unit updated");
      // refresh the dashboard headcount
      api.get(`/qms/dashboard?business_unit_id=${buId}&fy=${fy}&month=${month}`)
        .then((d) => setDash(d.data)).catch(() => {});
      api.get("/imports/employees/unresolved-business-units")
        .then((r) => setUnresolvedBU(r.data?.count || 0)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setEmpSavingId(null); }
  };
  const moveAllToAdmin = async () => {
    setEmpSavingId("bulk");
    try {
      const { data } = await api.post("/qms/bu-employees/move-unassigned-to-admin");
      toast.success(`Moved ${data.moved} staff to Administration`);
      const { data: fresh } = await api.get(`/qms/bu-employees?business_unit_id=${buId}`);
      setEmpData(fresh);
      api.get(`/qms/dashboard?business_unit_id=${buId}&fy=${fy}&month=${month}`)
        .then((d) => setDash(d.data)).catch(() => {});
      api.get("/imports/employees/unresolved-business-units")
        .then((r) => setUnresolvedBU(r.data?.count || 0)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Move failed"); }
    finally { setEmpSavingId(null); }
  };

  useEffect(() => {
    if (!loaded || !can("performance", "view") || !buId) return;
    setBusy(true);
    const params = `business_unit_id=${buId}&fy=${fy}&month=${month}`;
    Promise.all([
      api.get(`/qms/dashboard?${params}`),
      api.get(`/qms/bonus-breakdown?${params}`),
    ]).then(([dRes, bRes]) => {
      setDash(dRes.data);
      setBreakdown(bRes.data);
      setContext({
        module: "performance",
        summary: `Dashboard for ${dRes.data.business_unit.code} ${month}`,
        bu_score: dRes.data.hero.bu_score_pct,
        employees: dRes.data.hero.employees,
        bonus: dRes.data.hero.bonus,
        kra_scores: {
          financials: dRes.data.kras.financials.score_pct,
          safety: dRes.data.kras.safety.score_pct,
          customer: dRes.data.kras.customer.score_pct,
          performance: dRes.data.kras.performance.score_pct,
        },
      });
    }).catch(() => toast.error("Failed to load dashboard"))
      .finally(() => setBusy(false));
    return () => setContext(null);
    /* eslint-disable-next-line */
  }, [buId, fy, month, loaded]);

  if (!loaded) return <AppLayout title="Performance"><div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" /></AppLayout>;
  if (!can("performance", "view")) {
    return (
      <AppLayout title="Performance">
        <div className="max-w-md mx-auto mt-16 text-center">
          <ShieldAlert className="w-6 h-6 text-rose-700 inline" />
          <h2 className="font-display text-xl font-semibold mt-3">Access restricted</h2>
        </div>
      </AppLayout>
    );
  }

  const currentBU = bus.find((b) => b.id === buId);

  return (
    <AppLayout
      title="BU Performance"
      subtitle={dash ? `${dash.business_unit.code} · ${dash.business_unit.name} · ${month}` : "Loading…"}
      actions={
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <Select value={fy} onValueChange={setFy}>
            <SelectTrigger data-testid={TID.perfFYSelect} className="w-24 h-9 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {/* Dynamic FY list — always includes current AU FY plus
                  3 years back and 1 year forward. Prevents the "FY27
                  missing" issue that recurred every 1 July. */}
              {(() => {
                const now = new Date();
                const currentFy = now.getMonth() + 1 >= 7
                  ? now.getFullYear() + 1
                  : now.getFullYear();
                const list = [];
                for (let y = currentFy + 1; y >= currentFy - 3; y--) {
                  list.push(`FY${String(y).slice(-2)}`);
                }
                return list.map((f) => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ));
              })()}
            </SelectContent>
          </Select>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-28 h-9 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {monthsForFY(fy).map((m) => <SelectItem key={m.code} value={m.code}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={buId || ""} onValueChange={selectBU}>
            <SelectTrigger data-testid={TID.perfBUSelect} className="w-full sm:w-56 h-9 bg-white"><SelectValue placeholder="Business Unit" /></SelectTrigger>
            <SelectContent>
              {parentBus.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} · {b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-9"
                  data-testid="jsa-exemptions-btn"
                  onClick={() => setShowExemptions(true)}>
            Exempt Staff (Managers & Apprentices)
          </Button>
        </div>
      }
    >
      {unresolvedBU > 0 && (
        <div data-testid="perf-unresolved-bu-banner"
             className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-center gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
          <div className="text-xs text-amber-800 flex-1">
            <span className="font-semibold">{unresolvedBU} employee{unresolvedBU === 1 ? "" : "s"}</span> have an unresolved Business Unit and aren't counted in any BU's headcount or KPIs.
          </div>
          <button onClick={openEmpDialog} data-testid="perf-review-bu-btn"
                  className="text-xs font-medium text-amber-900 underline whitespace-nowrap mr-3">Review &amp; fix</button>
          <a href="/admin/imports" className="text-xs font-medium text-amber-900 underline whitespace-nowrap">Import Hub</a>
        </div>
      )}
      <Tabs defaultValue="dashboard">
        <TabsList className="bg-slate-50 border border-slate-200 p-1 h-auto">
          <TabsTrigger data-testid={TID.perfTab("dashboard")} value="dashboard" className="text-xs data-[state=active]:bg-white">Performance Dashboard</TabsTrigger>
          <TabsTrigger data-testid={TID.perfTab("compare")} value="compare" className="text-xs data-[state=active]:bg-white">Compare BUs</TabsTrigger>
          <TabsTrigger data-testid={TID.perfTab("bonus")}     value="bonus"     className="text-xs data-[state=active]:bg-white">Bonus Calculation</TabsTrigger>
        </TabsList>

        {/* ============ COMPARE ALL BUs ============ */}
        <TabsContent value="compare" className="mt-4">
          <BUCompare month={month} />
        </TabsContent>

        {/* ============ PERFORMANCE DASHBOARD ============ */}
        <TabsContent value="dashboard" className="mt-4">
          {busy && <div className="h-96 bg-white border border-slate-200 rounded-lg shimmer" />}
          {!busy && dash && (
            <>
              {/* Cross-BU early-warning ribbon — only visible when a
                   BU is drifting; clicking a card jumps to that BU. */}
              <TrendAlertsStrip endMonth={month}
                                currentBuId={buId}
                                onSelectBU={selectBU} />
              <div className="mb-4">
                <DataCompletenessPanel month={month} canEdit={can("performance", "edit")} />
              </div>
              {/* Hero row — mobile: BU Score full-width, then 2-up
                   Employees & Jobs. Desktop: 2/1/1 same as before. */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
                <div className="col-span-2 rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 text-center">
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">B.U Score</div>
                  <div className="font-display text-4xl sm:text-6xl font-bold text-gbe">{dash.hero.bu_score_pct.toFixed(2)}%</div>
                  <div className="mt-2 flex items-center justify-center gap-2 flex-wrap">
                    <Badge className={dash.hero.bonus > 0 ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}>
                      Bonus: {fmtCurrency(dash.hero.bonus)}
                    </Badge>
                    {dash.hero.safety_gate?.gated && (
                      <Badge data-testid="safety-gate-badge" className="bg-rose-100 text-rose-800">
                        Safety gate — Safety at {dash.hero.safety_gate.safety_pct_of_max}% (min {dash.hero.safety_gate.min_pct}%)
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 text-center cursor-pointer hover:border-gbe hover:shadow-sm transition"
                     onClick={openEmpDialog} data-testid="perf-employees-tile"
                     title="Click to review staff and move them to the correct Business Unit">
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Employees</div>
                  <div className="font-display text-3xl sm:text-5xl font-bold text-slate-900">{dash.hero.employees}</div>
                  <div className="text-[10px] text-gbe mt-1 font-medium">Click to review &amp; reassign</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 text-center">
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Jobs Completed</div>
                  <div className="font-display text-3xl sm:text-5xl font-bold text-slate-900">{dash.hero.jobs_completed}</div>
                  {dash.hero.jobs_completed === 0 && latestDataMonth && month > latestDataMonth && (
                    <div className="text-[10px] text-amber-600 mt-1 font-medium leading-tight" data-testid="jobs-no-import-hint">
                      No Completed Tasks imported for this month yet (latest: {latestDataMonth})
                    </div>
                  )}
                </div>
              </div>

              {/* 4 KRA panels — Financials gets its own richer treatment
                   (MYOB sales vs budget bar, GP $, GP % vs target). On
                   mobile these stack single-column; two columns from `sm:`.  */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FinancialsPanel data={dash.kras.financials} />
                <KRAPanel kraKey="safety"      data={dash.kras.safety}      onDrill={setDrillKpi} />
                <KRAPanel kraKey="customer"    data={dash.kras.customer}    onDrill={setDrillKpi} />
                <KRAPanel kraKey="performance" data={dash.kras.performance} onDrill={setDrillKpi} />
              </div>
              <KpiDrillDown open={!!drillKpi} onClose={() => setDrillKpi(null)}
                            buId={buId} month={month} kpi={drillKpi} />
              <JsaExemptions open={showExemptions} onClose={() => setShowExemptions(false)} />

              {/* 12-month rolling Sales/Budget/GP% trend — sits below the
                   KRA grid so BU heads see momentum, not just the current
                   month's snapshot. Hidden for roles without Financial access. */}
              {!dash.kras.financials?.restricted && <FinancialsTrendChart buId={buId} endMonth={month} />}
            </>
          )}
        </TabsContent>

        {/* ============ BONUS CALCULATION ============ */}
        <TabsContent value="bonus" className="mt-4">
          {busy && <div className="h-96 bg-white border border-slate-200 rounded-lg shimmer" />}
          {!busy && breakdown && (
            <BonusCalcTab breakdown={breakdown} />
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={empOpen} onOpenChange={setEmpOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="emp-bu-dialog">
          <DialogHeader>
            <DialogTitle>Employees — review &amp; reassign Business Unit</DialogTitle>
          </DialogHeader>
          {empLoading && <div className="h-40 shimmer rounded-lg" />}
          {!empLoading && empData && (
            <div className="space-y-5">
              {!canEditBU && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  You have view-only access — reassigning requires edit permission.
                </div>
              )}
              <EmpSection title={`In this business unit (${empData.members.length})`}
                          rows={empData.members} bus={empData.business_units}
                          onChange={reassignEmp} savingId={empSavingId} disabled={!canEditBU} />
              {empData.unassigned.length > 0 && (
                <div>
                  {canEditBU && (
                    <div className="flex justify-end mb-2">
                      <Button size="sm" variant="outline" onClick={moveAllToAdmin}
                              disabled={empSavingId === "bulk"} data-testid="emp-move-all-admin"
                              className="text-xs h-8">
                        {empSavingId === "bulk" && <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />}
                        Move all to Administration
                      </Button>
                    </div>
                  )}
                  <EmpSection title={`Unassigned / no business unit (${empData.unassigned.length})`}
                              rows={empData.unassigned} bus={empData.business_units}
                              onChange={reassignEmp} savingId={empSavingId} disabled={!canEditBU}
                              highlight />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function BonusCalcTab({ breakdown }) {
  const CATEGORY_LABEL = { safety: "Safety", customer: "Customer Service", performance: "Performance", financial: "Financial" };
  const byCat = useMemo(() => {
    const acc = { safety: [], customer: [], performance: [], financial: [] };
    breakdown.kpis.forEach((k) => acc[k.category].push(k));
    return acc;
  }, [breakdown]);

  return (
    <div className="space-y-4">
      {/* Formula ribbon — stack on mobile, side-by-side from `sm:` */}
      <div className="rounded-2xl bg-gbe text-white p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-white/60 mb-1">Bonus formula</div>
          <div className="font-display text-base sm:text-lg">MAX(0, Total Score % − Threshold %) × $ per pp</div>
          <div className="font-mono text-xs sm:text-sm text-white/80 mt-2 break-all">
            {breakdown.safety_gate?.gated ? (
              <span data-testid="bonus-safety-gated">
                Safety gate applied — Safety earned {breakdown.safety_gate.safety_pct_of_max}% of its weight
                (minimum {breakdown.safety_gate.min_pct}%) → bonus <span className="text-rose-300 font-semibold">$0.00</span>
              </span>
            ) : (
              <>MAX(0, {breakdown.total_score_pct}% − {breakdown.threshold_pct}%) × ${breakdown.rate_per_pp} = <span className="text-gbe-accent font-semibold">${breakdown.bonus.toFixed(2)}</span></>
            )}
          </div>
          {breakdown.safety_gate?.enabled && !breakdown.safety_gate?.gated && (
            <div className="text-[11px] text-white/50 mt-1.5">
              Safety gate: bonus only payable when Safety earns ≥ {breakdown.safety_gate.min_pct}% of its weight
              (currently {breakdown.safety_gate.safety_pct_of_max}%).
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 sm:flex sm:items-center gap-3 sm:gap-6 text-center">
          <div>
            <div className="text-xs uppercase tracking-wider text-white/60">Total Score</div>
            <div className="font-display text-2xl sm:text-3xl">{breakdown.total_score_pct}%</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-white/60">Over Threshold</div>
            <div className="font-display text-2xl sm:text-3xl">{breakdown.over_threshold_pp}pp</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-white/60">Bonus</div>
            <div className="font-display text-2xl sm:text-3xl text-gbe-accent">${breakdown.bonus.toFixed(2)}</div>
          </div>
        </div>
      </div>

      {/* Category summary — 2 cols on mobile, 4 on desktop */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Object.keys(CATEGORY_LABEL).map((c) => {
          const total = breakdown.category_totals[c];
          const weight = breakdown.category_weights[c];
          const ratio = weight ? (total / weight) * 100 : 0;
          return (
            <div key={c} className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">{CATEGORY_LABEL[c]}</div>
              <div className="font-display text-2xl mt-1">{total.toFixed(2)}%</div>
              <div className="text-xs text-slate-500">of {weight}% max ({ratio.toFixed(0)}%)</div>
              <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-gbe" style={{ width: `${Math.min(100, ratio)}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* KPI breakdown table */}
      {Object.entries(byCat).map(([cat, kpis]) => (
        <div key={cat} className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <div className="px-4 py-2.5 bg-gbe text-white text-sm font-semibold">{CATEGORY_LABEL[cat]}</div>
          <div className="grid grid-cols-6 gap-2 min-w-[640px] px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">
            <div className="col-span-2">KPI</div>
            <div className="text-right">Raw value</div>
            <div className="text-right">Target</div>
            <div className="text-right">Max weight</div>
            <div className="text-right">Earned</div>
          </div>
          <div className="divide-y divide-slate-100">
            {kpis.map((k) => (
              <div key={k.kpi_code} className="grid grid-cols-6 gap-2 min-w-[640px] px-4 py-2 text-sm tabular-nums">
                <div className="col-span-2 text-slate-900">{k.kpi_name}</div>
                <div className="text-right text-slate-700">{k.raw_value ?? "—"}</div>
                <div className="text-right text-slate-500">{k.direction === "higher_better" ? "≥" : "≤"} {k.target}</div>
                <div className="text-right text-slate-500">{k.max_weight_pct.toFixed(2)}%</div>
                <div className={`text-right font-semibold ${k.pct_of_max >= 100 ? "text-emerald-700" : k.pct_of_max >= 50 ? "text-slate-900" : "text-rose-700"}`}>
                  {k.earned_pct.toFixed(2)}%
                  <span className="text-xs text-slate-400 ml-1.5">({k.pct_of_max.toFixed(0)}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
